import { useCallback, useRef, type KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { SETTINGS_TREE, type TopNavId } from './settings-tree'
import { nextRovingIndex } from './nextRovingIndex'
import './PreferencesTopNav.scss'

interface PreferencesTopNavProps {
  activeId: TopNavId
  onSelect: (id: TopNavId) => void
}

/**
 * Horizontal top navigation showing the four primary Settings categories.
 * Selected tab gets a `muted` chip background and bolder label weight;
 * idle tabs are semibold-muted-foreground.
 *
 * Keyboard support follows the ARIA Tabs pattern (auto-activation):
 * - `ArrowLeft` / `ArrowRight` cycle through tabs (wrapping at the ends)
 * - `Home` / `End` jump to the first / last tab
 * - Roving `tabIndex`: only the active tab is in the document tab order;
 *   inactive tabs are programmatically focusable via the arrow keys
 */
export function PreferencesTopNav({ activeId, onSelect }: PreferencesTopNavProps) {
  const { t } = useTranslation()
  const containerRef = useRef<HTMLElement | null>(null)
  const activeIndex = SETTINGS_TREE.findIndex((entry) => entry.id === activeId)

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      const next = nextRovingIndex(event.key, activeIndex, SETTINGS_TREE.length, 'horizontal')
      if (next === null) return
      event.preventDefault()
      const nextId = SETTINGS_TREE[next].id as TopNavId
      onSelect(nextId)
      // Focus follows selection — find the button by id and shift focus
      // so subsequent arrow presses operate on the newly active tab.
      requestAnimationFrame(() => {
        const button = containerRef.current?.querySelector<HTMLElement>(
          `[data-topnav-id="${nextId}"]`,
        )
        button?.focus()
      })
    },
    [activeIndex, onSelect],
  )

  return (
    <nav
      ref={containerRef}
      className="preferences-topnav"
      role="tablist"
      aria-label={t('settings.title')}
      aria-orientation="horizontal"
      onKeyDown={handleKeyDown}
    >
      {SETTINGS_TREE.map((entry) => {
        const Icon = entry.icon
        const isActive = entry.id === activeId
        const label = t(entry.i18nKey)
        return (
          <button
            key={entry.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            tabIndex={isActive ? 0 : -1}
            data-topnav-id={entry.id}
            className={`preferences-topnav__tab ${isActive ? 'preferences-topnav__tab--active' : ''}`.trim()}
            onClick={() => onSelect(entry.id as TopNavId)}
          >
            <span className="preferences-topnav__icon">
              <Icon size={24} strokeWidth={1.6} />
            </span>
            {/* `data-text` feeds the invisible ::before sizer in SCSS — it
                pre-reserves the width of the boldest variant so the tab
                doesn't jitter when font-weight flips between 500 and 600
                on selection. */}
            <span className="preferences-topnav__label" data-text={label}>
              {label}
            </span>
          </button>
        )
      })}
    </nav>
  )
}

